import { create } from 'zustand';

import type { ChartDataDigest } from './turn-outcome-digest';

type GlobalAiChartPreviewPhase =
  | 'idle'
  | 'mounting'
  | 'applying_filters'
  | 'filters_ready'
  | 'fetching'
  | 'success'
  | 'error';

export type GlobalAiChartPreviewDataBranch =
  | 'snowflake'
  | 'multi'
  | 'main'
  | 'map';

export type GlobalAiChartPreviewRuntimeSnapshot = {
  phase: GlobalAiChartPreviewPhase;
  filterApplyError?: string;
  /**
   * Companies the user's trial excludes, when that is why the apply failed.
   * Present means the error is a PLAN limit, not a fault: the portal renders
   * the shared `TrialCompanyRestrictionMessage` (same wording and upgrade link
   * as the toast) instead of `filterApplyError`'s plain text.
   */
  filterApplyBlockedCompanies?: string[];
  /** Short line for blocked / in-flight data layer (metric, branch, taxonomy, urql status). */
  providerDiagnostics?: string;
  dataProviderBranch?: GlobalAiChartPreviewDataBranch;
  lastDataProviderStatus?: string;
  timeline?: GlobalAiChartPreviewTimelineEvent[];
  /** Compact data digest for narration; set once provider data settles. */
  dataDigest?: ChartDataDigest;
  /** Original user prompt for this turn (for the narration request). */
  narrationPrompt?: string;
  /**
   * Narration lifecycle for the line under the chart: 'streaming' renders a
   * skeleton until text arrives, 'shown' keeps the final text, 'failed'
   * renders nothing (fail-silent).
   */
  narrationStatus?: 'streaming' | 'shown' | 'failed';
  /** Accumulated narration text (replaces the skeleton as it streams in). */
  narrationText?: string;
};

export type GlobalAiChartPreviewTimelineEvent = {
  atMs: number;
  event: string;
};

export type GlobalAiPipelineTimelineEvent = {
  atMs: number;
  event: string;
};

function createIdleChartPreviewRuntime(): GlobalAiChartPreviewRuntimeSnapshot {
  return { phase: 'idle' };
}

/** Timeline entries kept per message — old entries are trimmed newest-last. */
const TIMELINE_CAP = 30;

/**
 * Chart-preview runtime in a vanilla zustand store instead of provider
 * `useState`.
 *
 * WHY: the turn runner must WAIT for a chart's data to settle
 * (`waitForChartSettle`), and that waiter is not a React consumer — provider
 * state signals a change only by re-rendering, which it can observe only by
 * polling a ref mirror. A store notifies subscribers synchronously per write,
 * so the settle wait is event-driven — same idiom as `waitForCommandComplete`.
 *
 * The provider still subscribes (via the hook) and re-exposes the SAME
 * context API (`chartPreviewRuntimeByMessageId`, `mergeChartPreviewRuntime`,
 * `addChartPreviewTimelineEvent`), so components are unaffected.
 *
 * Reset semantics: the provider resets this store as it mounts (lazy
 * initializer) — fresh state per provider mount, and no cross-test leakage
 * through the module-global store. Two simultaneously mounted providers would
 * share and clobber one store — single-provider assumption, same as the
 * conversation-state store.
 */
type GlobalAiChartPreviewRuntimeStore = {
  runtimeByMessageId: Readonly<
    Record<string, GlobalAiChartPreviewRuntimeSnapshot>
  >;
  /** Shallow-merge `patch` into the message's snapshot (no-op when nothing changes). */
  mergeChartPreviewRuntime: (
    messageId: string,
    patch: Partial<GlobalAiChartPreviewRuntimeSnapshot>
  ) => void;
  addChartPreviewTimelineEvent: (messageId: string, event: string) => void;
  resetChartPreviewRuntime: () => void;
};

export const useGlobalAiChartPreviewRuntimeStore =
  create<GlobalAiChartPreviewRuntimeStore>()((set, get) => ({
    runtimeByMessageId: {},

    mergeChartPreviewRuntime: (messageId, patch) => {
      const prev = get().runtimeByMessageId;
      const current = prev[messageId] ?? createIdleChartPreviewRuntime();
      const next: GlobalAiChartPreviewRuntimeSnapshot = {
        ...current,
        ...patch,
      };
      // Skip the write when the patch changes nothing — subscribers (the
      // provider render and any settle waits) shouldn't churn on identical
      // provider re-reports.
      const changed = Object.keys(patch).some((key) => {
        const typedKey = key as keyof GlobalAiChartPreviewRuntimeSnapshot;
        return current[typedKey] !== next[typedKey];
      });
      if (!changed) {
        return;
      }
      set({ runtimeByMessageId: { ...prev, [messageId]: next } });
    },

    addChartPreviewTimelineEvent: (messageId, event) => {
      const prev = get().runtimeByMessageId;
      const current = prev[messageId] ?? createIdleChartPreviewRuntime();
      const timeline = [
        ...(current.timeline ?? []),
        { atMs: Date.now(), event },
      ];
      const trimmed =
        timeline.length > TIMELINE_CAP
          ? timeline.slice(timeline.length - TIMELINE_CAP)
          : timeline;
      set({
        runtimeByMessageId: {
          ...prev,
          [messageId]: { ...current, timeline: trimmed },
        },
      });
    },

    resetChartPreviewRuntime: () => {
      set({ runtimeByMessageId: {} });
    },
  }));

/**
 * A snapshot the settle wait treats as terminal: the data layer either
 * delivered a digest or failed. A `success` phase WITHOUT a digest is not
 * terminal — providers can report success before the payload lands.
 */
function terminalSnapshot(
  snapshot: GlobalAiChartPreviewRuntimeSnapshot | undefined
): GlobalAiChartPreviewRuntimeSnapshot | undefined {
  if (!snapshot) return undefined;
  if (snapshot.phase === 'error') return snapshot;
  if (snapshot.phase === 'success' && snapshot.dataDigest) return snapshot;
  return undefined;
}

/**
 * Wait for the chart message's runtime to reach a terminal state (`success`
 * with a digest, or `error`). Resolves with the terminal snapshot; on timeout
 * or abort (drawer closed) it resolves with whatever snapshot exists at that
 * moment (possibly undefined).
 */
export function waitForChartSettle(
  messageId: string,
  timeoutMs: number,
  signal?: AbortSignal
): Promise<GlobalAiChartPreviewRuntimeSnapshot | undefined> {
  const read = () =>
    useGlobalAiChartPreviewRuntimeStore.getState().runtimeByMessageId[
      messageId
    ];

  const immediate = terminalSnapshot(read());
  if (immediate) return Promise.resolve(immediate);
  if (signal?.aborted) return Promise.resolve(read());

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      cleanup();
      resolve(read());
    }, timeoutMs);
    const onAbort = () => {
      cleanup();
      resolve(read());
    };
    const cleanup = () => {
      clearTimeout(timer);
      unsubscribe();
      signal?.removeEventListener('abort', onAbort);
    };
    signal?.addEventListener('abort', onAbort);
    const unsubscribe = useGlobalAiChartPreviewRuntimeStore.subscribe(
      (state) => {
        const hit = terminalSnapshot(state.runtimeByMessageId[messageId]);
        if (hit) {
          cleanup();
          resolve(hit);
        }
      }
    );
  });
}
